/**
 * My profile → Emails: the reminders and summaries a person can switch off.
 *
 * Every email ends with a link here (`with_settings_link` in
 * backend-python/services/notification_dispatcher.py → /profile?section=emails;
 * a query, because the login round trip drops a #fragment). Only the server's
 * SWITCHABLE_EVENTS appear, so keep the two lists in step. Mail about
 * something that happened to you is always sent (#757). Turning a switch off
 * stops the EMAIL and leaves the bell; turning it on restores both, which
 * repairs digest rows the old switch wrote with both channels off.
 *
 * The weekly digest switch used to sit in the talent sidebar only, so council
 * and admins — who get the digest by default — had no way to stop it.
 */

import { useEffect, useRef } from 'react'
import type { ReactElement } from 'react'
import {
  Button,
  Caption1,
  Subtitle2,
  Switch,
  Tooltip,
  makeStyles,
  tokens,
} from '@fluentui/react-components'
import {
  ClockAlarmRegular,
  HourglassHalfRegular,
  InfoRegular,
  NewsRegular,
  PeopleQueueRegular,
} from '@fluentui/react-icons'
import { useSearchParams } from 'react-router-dom'
import { useEffectiveUser } from '../hooks/useEffectiveUser'
import {
  useNotificationPreferences,
  useUpdateNotificationPreference,
} from '../hooks/useNotifications'
import { apiErrorMessage } from '../lib/apiError'
import { useToast } from './ToastProvider'

interface EmailSwitch {
  event: string
  /** The preference row's type. The server reads by event alone. */
  type: string
  label: string
  hint: string
  icon: ReactElement
}

export const EMAIL_SWITCHES: EmailSwitch[] = [
  {
    event: 'fa.digest.weekly',
    type: 'focus_area',
    label: 'Weekly pipeline digest',
    hint: 'Mondays: what was submitted, what opened, what is closing, and what closed with nobody approved.',
    icon: <NewsRegular />,
  },
  {
    event: 'fa.demand.stale_under_review',
    type: 'focus_area',
    label: 'Stalled demands',
    hint: 'Mondays: demands you look after that have not moved for 14 days.',
    icon: <HourglassHalfRegular />,
  },
  {
    event: 'fa.demand.posted_no_apps',
    type: 'focus_area',
    label: 'No applicants',
    hint: 'Mondays: demands you look after that passed their application deadline with nobody approved.',
    icon: <PeopleQueueRegular />,
  },
  {
    event: 'demand.closing_soon',
    type: 'demand',
    label: 'Closing soon',
    hint: 'Mondays: demands that ask for a skill you follow, deadline this week.',
    icon: <ClockAlarmRegular />,
  },
]

// The digest is the one subscription: on by default for these roles, off for
// everyone else (derived on the server at send time, weekly_digest.py).
const DIGEST_DEFAULT_ROLES = ['council', 'admin', 'super_admin']

const useStyles = makeStyles({
  panel: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalS,
    maxWidth: '1080px',
    marginTop: tokens.spacingVerticalXL,
    padding: tokens.spacingHorizontalL,
    borderRadius: tokens.borderRadiusLarge,
    backgroundColor: tokens.colorNeutralBackground2,
    scrollMarginTop: tokens.spacingVerticalXXL,
  },
  list: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))',
    gap: tokens.spacingHorizontalS,
    margin: 0,
    padding: 0,
    listStyle: 'none',
  },
  row: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalXS,
    padding: `${tokens.spacingVerticalXS} ${tokens.spacingHorizontalS}`,
    borderRadius: tokens.borderRadiusMedium,
    backgroundColor: tokens.colorNeutralBackground1,
  },
  icon: { display: 'flex', fontSize: '20px', color: tokens.colorBrandForeground1 },
  label: { fontWeight: tokens.fontWeightSemibold },
  switch: { marginLeft: 'auto' },
  note: { color: tokens.colorNeutralForeground3 },
})

export function EmailSettings() {
  const styles = useStyles()
  const effectiveUser = useEffectiveUser()
  const { data: prefs } = useNotificationPreferences()
  const updatePref = useUpdateNotificationPreference()
  const { showToast } = useToast()
  const [params] = useSearchParams()
  const sectionRef = useRef<HTMLElement>(null)

  useEffect(() => {
    if (params.get('section') === 'emails') sectionRef.current?.scrollIntoView?.({ block: 'start' })
  }, [params])

  const isOn = (s: EmailSwitch): boolean => {
    const rows = (prefs ?? []).filter((p) => p.event_key === s.event)
    const latest = rows.reduce<(typeof rows)[number] | undefined>(
      (best, p) => (!best || p.id > best.id ? p : best),
      undefined,
    )
    if (latest) return latest.email_enabled
    if (s.event === 'fa.digest.weekly') {
      return DIGEST_DEFAULT_ROLES.includes(effectiveUser?.role ?? '')
    }
    return true
  }

  const toggle = (s: EmailSwitch, checked: boolean) =>
    updatePref.mutate(
      {
        type: s.type,
        eventKey: s.event,
        body: checked ? { email_enabled: true, in_app_enabled: true } : { email_enabled: false },
      },
      {
        onError: (err) =>
          showToast({
            title: 'Could not save that setting',
            body: apiErrorMessage(err),
            intent: 'error',
          }),
      },
    )

  return (
    <section ref={sectionRef} id="emails" className={styles.panel} aria-labelledby="emails-title">
      <Subtitle2 as="h2" id="emails-title">
        Emails
      </Subtitle2>
      <ul className={styles.list}>
        {EMAIL_SWITCHES.map((s) => (
          <li key={s.event} className={styles.row}>
            <span className={styles.icon} aria-hidden="true">
              {s.icon}
            </span>
            <span className={styles.label}>{s.label}</span>
            <Tooltip content={s.hint} relationship="label">
              <Button appearance="transparent" size="small" icon={<InfoRegular />} />
            </Tooltip>
            <Switch
              className={styles.switch}
              aria-label={s.label}
              checked={isOn(s)}
              disabled={updatePref.isPending}
              onChange={(_, d) => toggle(s, d.checked)}
            />
          </li>
        ))}
      </ul>
      <Caption1 className={styles.note}>
        Mail about your own demands, applications and invitations is always sent.
      </Caption1>
    </section>
  )
}
